import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageTransition } from "@/components/common/PageTransition";
import { usePageHeading } from "@/components/layout/AppLayout";
import { revenueApi } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { TID } from "@/constants/testIds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Subscribers() {
  usePageHeading("Subscribers", "Active subscriptions & billing");
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planFilter, setPlanFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const response = await revenueApi.subscribers();
        const normalized = (response || []).map((sub) => ({
          ...sub,
          clinic: sub.clinicName ?? sub.clinic ?? "Clinic",
          email: sub.email ?? "",
          owner: sub.owner ?? "",
          plan: sub.planName ?? sub.plan ?? "Trial",
          status: (sub.status ?? "ACTIVE").toLowerCase(),
          amount: sub.amount ?? 0,
          renewsAt: sub.endDate ?? sub.renewsAt ?? null,
        }));
        setSubs(normalized);
      } catch {
        toast.error("Failed to load subscribers");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const rows = planFilter === "all" ? subs : subs.filter((s) => s.plan === planFilter);

  const columns = [
    {
      key: "clinic",
      header: "Subscriber",
      sortable: true,
      searchable: true,
      render: (r) => (
        <div>
          <p className="font-semibold">{r.clinic}</p>
          <p className="text-xs text-muted-foreground">{r.email}</p>
        </div>
      ),
    },
    { key: "owner", header: "Owner", sortable: true, searchable: true, render: (r) => <span className="text-muted-foreground">{r.owner}</span> },
    { key: "plan", header: "Plan", sortable: true, render: (r) => <span className="font-medium">{r.plan}</span> },
    { key: "seats", header: "Seats", sortable: true },
    { key: "amount", header: "Amount", sortable: true, render: (r) => <span className="font-semibold text-gold">{formatCurrency(r.amount)}/mo</span> },
    { key: "status", header: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: "renewsAt", header: "Renews", sortable: true, sortAccessor: (r) => new Date(r.renewsAt).getTime(), render: (r) => <span className="text-muted-foreground">{formatDate(r.renewsAt)}</span> },
  ];

  const planFilterEl = (
    <Select value={planFilter} onValueChange={setPlanFilter}>
      <SelectTrigger className="h-10 w-[150px] border-border bg-secondary/30">
        <SelectValue placeholder="Plan" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All plans</SelectItem>
        <SelectItem value="Starter">Starter</SelectItem>
        <SelectItem value="Professional">Professional</SelectItem>
        <SelectItem value="Enterprise">Enterprise</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <PageTransition className="space-y-6">
      <div data-testid={TID.subscribersPage} />
      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        searchPlaceholder="Search subscribers…"
        searchTestId={TID.subscriberSearch}
        filters={planFilterEl}
        emptyTitle="No subscribers found"
        emptyDescription="Try adjusting your search or plan filter."
      />
    </PageTransition>
  );
}
